<template>
  <div>
    <div class="tabs d-flex">
      <div
        class="leaderboard-tab"
        :class="{
          selected: selectedTab === 'dataScientists',
          'numerai-paper': selectedTab === 'dataScientists',
          square: selectedTab === 'dataScientists',
          inactive: selectedTab !== 'dataScientists',
        }"
        @click="onTabChange('dataScientists')"
      >
        Data Scientists
      </div>
      <div
        class="leaderboard-tab"
        :class="{
          selected: selectedTab === 'models',
          'numerai-paper': selectedTab === 'models',
          square: selectedTab === 'models',
          inactive: selectedTab !== 'models',
        }"
        @click="onTabChange('models')"
      >
        Models
      </div>
      <div class="search ml-auto d-none d-md-flex">
        <v-icon small class="search-icon">mdi-magnify</v-icon>
        <input
          class="search-input"
          placeholder="Search"
          :value="searchFilter"
          @input="updateSearchFilter"
        />
      </div>
    </div>
    <div class="stats-container">
      <div class="stats-body numerai-paper square">
        <StatisticBlock
          :label="
            `Staked ${
              selectedTab === 'dataScientists' ? 'Data Scientists' : 'Models'
            }`
          "
          class="brand-label"
          :value="totalNumberStakes"
          :value-tooltip="
            `Staked ${
              selectedTab === 'models' ? 'models' : 'data scientists'
            } that control ` +
              (isAppCryptoSignals()
                ? 'the Numerai Crypto Meta Model'
                : `Numerai's hedge fund`)
          "
          :loading="isFetchingOverviewStats"
        />
        <StatisticBlock
          label="Staked NMR At Risk"
          class="brand-label"
          :value="totalAtRiskToString"
          :value-tooltip="totalAtRiskToUSDToString"
          :loading="isFetchingOverviewStats"
          nmr
        />
        <StatisticBlock
          label="Avg 1Y Return"
          class="brand-label"
          :value="oneYearReturnsToString"
          :value-tooltip="`Average 1 year return on NMR staked`"
          :loading="isFetchingOverviewStats"
          :positive="parseFloat(oneYearReturns) > 0"
          :negative="parseFloat(oneYearReturns) < 0"
        />
      </div>
    </div>
    <div class="leaderboard-table-container numerai-paper square">
      <div class="search mt-2 d-flex d-md-none">
        <v-icon small class="search-icon">mdi-magnify</v-icon>
        <input
          class="search-input"
          placeholder="Search"
          :value="searchFilter"
          @input="updateSearchFilter"
        />
      </div>
      <div v-if="isFetchingLeaderboard" class="no-leaderboard-search-results">
        <NumeraiLoader />
      </div>
      <div v-else class="leaderboard-table">
        <div class="table-header-group">
          <div class="table-cell table-header-cell rank">
            RANK
          </div>
          <div class="table-cell table-header-cell text-left cell-userinfo">
            NAME
          </div>
          <div
            v-for="(obj, scoreKey) in tournamentScoresConfig"
            :key="scoreKey"
            class="head clickable table-cell table-header-cell"
            :class="[
              {
                colSelected: sortBy == scoreKey + 'Rep',
                sortReversed: sortReversed,
                [scoreKey]: true,
              },
              ...obj.classList,
            ]"
            @click="onSort(scoreKey + 'Rep')"
          >
            <NumeraiTooltip :text="obj.repTooltip">
              {{ obj.displayName }}
            </NumeraiTooltip>
          </div>
          <div
            class="table-cell head clickable table-header-cell head-big stake"
            :class="{
              colSelected: sortBy == 'nmrStaked',
              sortReversed: sortReversed,
            }"
            @click="onSort('nmrStaked')"
          >
            <NumeraiTooltip :text="stakedTip">Stake</NumeraiTooltip>
          </div>
          <div
            v-if="selectedTab === 'models'"
            class="table-cell head clickable table-header-cell return-col"
            :class="{
              colSelected: sortBy == 'return1d',
              sortReversed: sortReversed,
            }"
            @click="onSort('return1d')"
          >
            <NumeraiTooltip :text="return3mTip">1D</NumeraiTooltip>
          </div>
          <div
            class="table-cell head clickable table-header-cell return-col"
            :class="{
              colSelected: sortBy == 'return3m',
              sortReversed: sortReversed,
            }"
            @click="onSort('return3m')"
          >
            <NumeraiTooltip :text="return3mTip">3M</NumeraiTooltip>
          </div>
          <div
            class="
              table-cell
              head
              clickable
              table-header-cell
              return-col
              last-col
            "
            :class="{
              colSelected: sortBy == 'return1y',
              sortReversed: sortReversed,
            }"
            @click="onSort('return1y')"
          >
            <NumeraiTooltip :text="return1yTip">1Y</NumeraiTooltip>
          </div>
          <div
            v-if="selectedTab === 'dataScientists'"
            class="table-cell head clickable table-header-cell return-col"
            :class="{
              colSelected: sortBy == 'returnAllTime',
              sortReversed: sortReversed,
            }"
            @click="onSort('returnAllTime')"
          >
            <NumeraiTooltip :text="returnAllTimeTip">All</NumeraiTooltip>
          </div>
        </div>
        <tbody class="table-row-group">
          <template v-for="row in visibleEntries">
            <div
              :key="row.username"
              class="table-row"
              :class="{ dark: isDarkMode }"
            >
              <div class="rank table-cell">
                {{ row.rank }}
              </div>
              <router-link
                :to="
                  `/${selectedTab === 'dataScientists' ? '~' : ''}${
                    row.username
                  }`
                "
                class="table-cell cell-userinfo"
              >
                <div class="d-flex align-center">
                  <NumeraiTooltip :text="row.title">
                    <div
                      class="profile-picture-container hidden-on-mobile"
                      :class="{
                        default: !row.profileUrl,
                        [row.title]:
                          !row.profileUrl &&
                          row.title &&
                          selectedTab === 'dataScientists',
                      }"
                    >
                      <img
                        v-if="row.profileUrl && !isMobile"
                        class="profile-picture hidden-on-mobile"
                        :class="{
                          [row.title]:
                            row.title && selectedTab === 'dataScientists',
                        }"
                        :style="{ backgroundImage: `url('${row.profileUrl}')` }"
                        loading="lazy"
                      />
                    </div>
                  </NumeraiTooltip>
                  {{ row.username }}
                  <NumeraiTooltip
                    v-if="row.team && !isMobile"
                    text="Model created by the Numerai team"
                  >
                    <img
                      src="/img/Numerai-Sigil-Black.png"
                      class="team-badge invert-on-darkmode hidden-on-mobile"
                    />
                  </NumeraiTooltip>
                </div>
              </router-link>
              <div
                v-for="(obj, scoreKey) in tournamentScoresConfig"
                :key="scoreKey"
                class="table-cell data"
                :class="[
                  { 'null-number': !row[`${scoreKey}RepFormatted`] },
                  ...obj.classList,
                ]"
              >
                {{ row[`${scoreKey}RepFormatted`] }}
              </div>
              <div
                class="table-cell data stake"
                :class="{
                  'color-nmr': parseFloat(row.nmrStaked) && row.is_active,
                  'color-subtle': parseFloat(row.nmrStaked) && !row.is_active,
                  'null-number': !parseFloat(row.nmrStaked),
                }"
                :title="stakeRowTitle(row)"
              >
                {{
                  parseFloat(row.nmrStaked)
                    ? row.nmrStakedFormatted + ' NMR'
                    : ''
                }}
              </div>
              <div
                v-for="returns in returnCols"
                :key="returns[0]"
                class="table-cell data hide-tick
                return-col"
                :title="returnTitle(returns[0])"
                :class="{
                  delta: row[returns[1]],
                  'color-positive':
                    row[returns[1]] && Number(row[returns[1]]) > 0,
                  'color-negative':
                    row[returns[1]] && Number(row[returns[1]]) < 0,
                  'null-number': !row[returns[1]],
                }"
              >
                {{ row[returns[1]] ? removeMinusSign(row[returns[2]]) : '' }}
              </div>
            </div>
          </template>
        </tbody>
      </div>
    </div>
    <div
      v-if="
        !isFetchingLeaderboard && isLoaded && entries && entries.length == 0
      "
      class="no-leaderboard-search-results"
    >
      <div>No matching AIs</div>
    </div>
    <div
      v-if="isLoaded && showLoadMore"
      class="text-center mt-5"
      @click="loadMore"
    >
      <button class="numerai-button" :disabled="isLoadingMoreRows">
        {{ isLoadingMoreRows ? 'Loading...' : 'Load more' }}
      </button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapActions } from 'vuex'
import NumeraiTooltip from '@/components/shared/NumeraiTooltip'
import NumeraiLoader from '@/components/shared/NumeraiLoader'
import StatisticBlock from '@/components/shared/StatisticBlock'
import {
  formatScore,
  formatPercent,
  formatCurrency,
  removeMinusSign,
  isAppSignals,
  isAppCryptoSignals,
  getTournamentScoresConfig,
  getTournamentNumber,
} from '@/lib/utils'
import ls from 'local-storage'

export default {
  components: {
    NumeraiTooltip,
    NumeraiLoader,
    StatisticBlock,
  },
  props: {
    loading: { type: Boolean, default: false },
    nmrPrice: { type: String, default: '' },
    isDarkMode: { type: Boolean, default: false },
    isMobile: { type: Boolean, default: false },
  },
  data() {
    return {
      modelLeaderboard: [],
      accountLeaderboard: [],
      isFetchingLeaderboard: false,
      limit: 101,
      rowsLimit: 100,
      loadMoreIncrement: 100, // added to rows limit when 'load all' clicked
      isLoadingMoreRows: false,
      entriesUnslicedSize: 0, // Rows in entries before slicing to rowsLimit
      stakingOnMMC: 'Staking on meta model contribution',
      stakingOnTC: 'Staking on True Contribution',
      stakedTip: 'The NMR staked on this model',
      return1dTip: 'Return on NMR stake today',
      returnAllTimeTip:
        'Return on NMR staked for all time including all payouts and burns',
      return1yTip:
        'Return on NMR staked 1 year ago including all payouts and burns',
      return3mTip:
        'Return on NMR staked 3 months ago including all payouts and burns',
      searchFilter: '',
      sortBy: 'mmcRep',
      sortDirection: 'desc',
      sortReversed: false,
      showLoadMore: true,
      overviewStats: null,
      isFetchingOverviewStats: false,
      selectedTab: 'dataScientists',
      modelLeaderboardEndpoints: {
        8: 'getLeaderboard',
        11: 'getSignalsLeaderboard',
        12: 'getCryptoSignalsLeaderboard',
      },
    }
  },
  computed: {
    ...mapState(['accountInfo']),
    highestEarnedTitle() {
      return null // 'Novice'
    },
    leaderboard() {
      return this.selectedTab === 'dataScientists'
        ? this.accountLeaderboard
        : this.modelLeaderboard
    },
    returnCols() {
      const cols = [
        ['return3mTip', 'return3m', 'return3mFormatted'],
        ['return1yTip', 'return1y', 'return1yFormatted'],
      ]

      if (this.selectedTab === 'models') {
        cols.unshift(['return1dTip', 'return1d', 'return1dFormatted'])
      } else {
        cols.push([
          'returnAllTimeTip',
          'returnAllTime',
          'returnAllTimeFormatted',
        ])
      }

      return cols
    },
    totalNumberStakes() {
      if (!this.overviewStats) {
        return null
      }
      if (!this.overviewStats.stakedSubmissions) {
        return null
      }
      if (this.selectedTab === 'models') {
        return this.overviewStats.stakedSubmissions.toLocaleString()
      } else {
        return this.overviewStats.stakedAccounts[0].count.toLocaleString()
      }
    },
    totalAtRisk() {
      if (!this.overviewStats) {
        return false
      }
      if (!this.overviewStats.totalAtRisk) {
        return false
      }
      return this.overviewStats.totalAtRisk
    },
    totalAtRiskToString() {
      const stake = this.totalAtRisk

      if (!stake) {
        return null
      }

      const total = parseFloat(stake) || 0
      if (Math.abs(total) < 1 && total !== 0) {
        // If number is a fraction, use toPrecision
        return total.toPrecision(3) + ' NMR'
      }
      // else round
      return _.round(total, 0).toLocaleString() + ' NMR'
    },
    totalAtRiskToUSDToString() {
      let totalStake = parseFloat(this.totalAtRisk) || 0
      if (!totalStake) {
        return null
      }
      return (
        '$' +
        _.round(
          totalStake * parseFloat(this.nmrPrice).toPrecision(3),
          2,
        ).toLocaleString()
      )
    },
    oneYearReturns() {
      if (!this.overviewStats) {
        return false
      }
      if (!this.overviewStats.returns) {
        return false
      }
      return parseFloat(this.overviewStats.returns.oneYear)
    },
    oneYearReturnsToString() {
      const avg = this.oneYearReturns
      if (!avg) {
        return null
      }
      return _.round(avg, 2).toLocaleString() + '%'
    },
    isLoaded() {
      return Array.isArray(this.leaderboard)
    },
    tournamentScoresConfig() {
      const filterKey = 'showLeaderboard'
      let config = getTournamentScoresConfig(getTournamentNumber(), filterKey)
      return config
    },
    entries() {
      return this.formatRows(this.leaderboard)
    },
    visibleEntries() {
      return [...this.entries.slice(0, this.rowsLimit)]
    },
  },
  async created() {
    this.selectedTab = ls.get('leaderboardTab') || 'dataScientists'
    this.fetchOverviewStats()
    this.fetchAccountLeaderboard({ initialFetch: true })
    this.fetchModelLeaderboard({})
  },
  methods: {
    formatScore,
    formatPercent,
    formatCurrency,
    removeMinusSign,
    isAppSignals,
    isAppCryptoSignals,
    getTournamentNumber,
    getTournamentScoresConfig,
    ...mapActions(['fetchAccountInfo']),
    async fetchOverviewStats() {
      this.isFetchingOverviewStats = true
      try {
        this.overviewStats = await this.$client.getTournamentOverview(
          getTournamentNumber(),
        )
      } catch (e) {
        console.error(e)
      } finally {
        this.isFetchingOverviewStats = false
      }
    },
    async fetchLeaderboard(options) {
      if (this.selectedTab === 'dataScientists') {
        return await this.fetchAccountLeaderboard(options)
      } else {
        return await this.fetchModelLeaderboard(options)
      }
    },
    async fetchAccountLeaderboard(options) {
      this.isFetchingLeaderboard = options.initialFetch

      let response = await this.$client.getAccountLeaderboard({
        limit: this.limit,
        offset: options.append ? this.rowsLimit : 0,
        orderBy: this.formatOrderBy(this.sortBy),
        direction: this.sortDirection,
        filterBy: this.searchFilter || null,
        tournament: getTournamentNumber(),
      })

      this.isFetchingLeaderboard = false

      if (response.length < this.limit) {
        this.showLoadMore = false
      } else {
        this.showLoadMore = true
      }

      // trim off the extra + 1
      response = response.slice(0, this.limit - 1)

      if (options.append) {
        this.accountLeaderboard = [...this.accountLeaderboard, ...response]
      } else {
        this.accountLeaderboard = response
      }
    },
    async fetchModelLeaderboard(options) {
      const leaderboardEndpoint = this.modelLeaderboardEndpoints[
        getTournamentNumber()
      ]
      const isModelsLeaderboard = true
      try {
        let response = await this.$client[leaderboardEndpoint]({
          limit: this.limit,
          offset: options.append ? this.rowsLimit : 0,
          orderBy: this.formatOrderBy(
            this.sortBy === 'v2_corr20Rep' ? 'corr20V2Rep' : this.sortBy,
            isModelsLeaderboard,
          ),
          direction: this.sortDirection,
          filterBy: this.searchFilter || null,
        })
        console

        if (response.length < this.limit) {
          this.showLoadMore = false
        } else {
          this.showLoadMore = true
        }

        // trim off the extra + 1
        response = response.slice(0, this.limit - 1)

        if (options.append) {
          this.modelLeaderboard = [...this.modelLeaderboard, ...response]
        } else {
          this.modelLeaderboard = response
        }
      } catch (e) {
        console.error(e)
      }
    },
    resetLeaderboardSettings() {
      this.rowsLimit = this.loadMoreIncrement
      this.sortBy = 'mmcRep'
      this.sortDirection = 'desc'
      this.sortReversed = false
      this.searchFilter = ''
      this.fetchSearchResults()
    },
    formatOrderBy(orderBy, isModelsLeaderboard) {
      if (isModelsLeaderboard) {
        switch (orderBy) {
          case 'return1d':
            return 'return1Day'
          case 'return3m':
            return 'return13Weeks'
          case 'return1y':
            return 'return52Weeks'
          default:
            return orderBy
        }
      } else {
        if (orderBy.slice(orderBy.length - 3, orderBy.length) === 'Rep') {
          return orderBy.split('Rep')[0]
        } else {
          return orderBy
        }
      }
    },
    formatRows(leaderboard) {
      const getFormattedScores = row =>
        Object.keys(this.tournamentScoresConfig).reduce((obj, scoreKey) => {
          obj[scoreKey + 'RepFormatted'] = formatScore(row[scoreKey + 'Rep'])
          return obj
        }, {})

      return leaderboard.map((row, index) => ({
        index: index,
        return1dFormatted: formatPercent(row.return1d),
        return3mFormatted: formatPercent(row.return3m),
        return1yFormatted: formatPercent(row.return1y),
        returnAllTimeFormatted: formatPercent(row.returnAllTime),
        nmrStakedFormatted: formatCurrency(row.nmrStaked),
        nmrStakedToUsd: formatCurrency(
          row.nmrStaked * parseFloat(this.nmrPrice),
        ),
        is_active: row.is_active || this.selectedTab === 'dataScientists',
        ...getFormattedScores(row),
        ...row,
      }))
    },
    stakeRowTitle(row) {
      if (parseFloat(row.nmrStaked)) {
        if (row.is_active) {
          return `$${row.nmrStakedToUsd}`
        } else {
          return 'Model has not submitted in recent rounds'
        }
      } else {
        return 'No Stake'
      }
    },
    returnTitle(key) {
      return this[key]
    },
    async loadMore() {
      if (this.isLoadingMoreRows) {
        return
      }

      this.isLoadingMoreRows = true
      await this.fetchLeaderboard({ append: true })
      this.isLoadingMoreRows = false
      this.rowsLimit += this.loadMoreIncrement
    },
    onSort(scoreColumn) {
      if (scoreColumn !== this.sortBy || this.sortDirection === 'asc') {
        this.sortDirection = 'desc'
        this.sortReversed = false
      } else {
        this.sortDirection = 'asc'
        this.sortReversed = true
      }
      this.sortBy = scoreColumn
      this.rowsLimit = this.loadMoreIncrement
      this.fetchLeaderboard({ append: false })
    },
    onTabChange(newTab) {
      this.resetLeaderboardSettings()
      this.selectedTab = newTab
      ls.set('leaderboardTab', newTab)
    },
    updateSearchFilter(event) {
      this.searchFilter = _.toLower(event.target.value)
      this.fetchSearchResults()
    },
    fetchSearchResults: _.debounce(function() {
      this.rowsLimit = this.loadMoreIncrement
      this.fetchLeaderboard({ append: false })
    }, 300),
  },
}
</script>

<!-- Important: I removed the `scoped` from the style tag
so the injected rows get styling, too. These styles are now global,
so should all be defined more precisely, eg, specifying that the
styles apply to children of .leaderboard-table -->
<style lang="scss">
.leaderboard-tab {
  padding: 10px 40px;
  font-size: 20px;
  font-weight: 700;
}

.leaderboard-tab.selected {
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
  z-index: 1;
  margin-bottom: -1px;
  border-bottom: 1px solid var(--paper-background-color);
}

.leaderboard-tab.inactive {
  margin: 1px;
  color: var(--color-subtle);
}

.leaderboard-tab:hover {
  cursor: pointer;
}

.stats-container {
  container-type: inline-size;
  container-name: stats;
}

.stats-body {
  padding: 10px 15px 15px;
}

@container stats (min-width: 780px) {
  .stats-body {
    display: flex;
    justify-content: space-around;
  }
}

.leaderboard-table-container {
  width: 100%;
  overflow: auto;
  padding: 4px 25px;
  container-type: inline-size;
  container-name: leaderboard;
}

.leaderboard-table {
  border: 2px solid var(--paper-background-color);
  table-layout: fixed;
  border-collapse: collapse;
  display: table;
  font-family: var(--ui-font);
  width: 100%;
  overflow-x: scroll;
}

.leaderboard-table .table-header-group {
  display: table-header-group;
  position: sticky;
  height: 48px;
  font-size: 0.8em;
  font-weight: 600;
  /* text-transform: uppercase; */
  font-family: var(--ui-font);
  width: 10%;
  text-align: right;
  background-color: var(--paper-background-color);
  color: var(--color-tertiary);
}

.leaderboard-table .table-header-cell.table-cell {
  display: table-cell;
  font-size: 12px;
  font-family: var(--brand-font);
  font-weight: 500;
}

.leaderboard-table .table-header-cell.table-cell.rank {
  text-align: center;
}

.leaderboard-table .table-row-group {
  display: table-row-group;
  background-color: var(--background-color);
}

.leaderboard-table .table-cell {
  padding: 0.8em 0.8em;
  vertical-align: middle;
  outline: none;
  border: 0;
  display: table-cell;
  white-space: nowrap;
  font-size: 14px;
  font-family: var(--number-font);
  width: 100px;
}

.leaderboard-table .table-header-cell.head-big {
  width: 150px;
}

.leaderboard-table .table-row {
  background-color: var(--paper-background-color);
  display: table-row;
  border-top: 1px solid var(--color-very-subtle);
}

.leaderboard-table a.table-row {
  text-decoration: none;
}

.leaderboard-table .rank {
  color: var(--color-secondary);
  font-weight: bold;
  width: 6em;
  max-width: 6em;
  min-width: 6em;
  text-align: center;
  cursor: auto;
}
.table-header-cell:first-child .rank {
  text-align: center;
}

.leaderboard-table .name {
  color: var(--color-secondary);
  cursor: auto;
}

.leaderboard-table .rank:hover {
  color: var(--color-secondary);
}

.table-cell.cell-userinfo {
  width: 200px;
  text-transform: uppercase;
  color: var(--color-secondary);
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  font-size: 13px;
  text-decoration: none;
}

.table-cell.cell-userinfo:hover {
  color: var(--color-secondary);
}

.leaderboard-table .badge-row {
  display: flex;
  text-transform: none;
}

.leaderboard-table .last-col {
  padding-right: 24px;
}

.leaderboard-table .profile-picture-container {
  width: 3em;
  height: 3em;
  min-width: 3em;
  min-height: 3em;
  max-width: 3em;
  max-height: 3em;
  margin-bottom: 0px;
  margin-right: 10px;
  border-radius: 4px;
  border: 1px solid var(--paper-border-color);
  background-color: var(--paper-background-color);
  overflow: hidden;
  background-size: cover;

  &.default {
    background-image: var(--default_profile_picture);
  }
}

.leaderboard-table .profile-picture {
  width: 3em;
  height: 3em;
  min-width: 3em;
  min-height: 3em;
  max-width: 3em;
  max-height: 3em;
  margin-left: -1px;
  margin-top: -1px; /* undoing the container border */
  background-color: var(--color-very-subtle);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.leaderboard-table .profile-picture-container,
.leaderboard-table .profile-picture {
  &.Grandmaster {
    border: 3px solid var(--color-grandmaster);
  }

  &.Master {
    border: 3px solid var(--color-master);
  }

  &.Expert {
    border: 3px solid var(--color-expert);
  }

  &.Researcher {
    border: 3px solid var(--color-researcher);
  }

  &.Contributor {
    border: 3px solid var(--color-contributor);
  }

  &.Apprentice {
    border: 3px solid var(--color-apprentice);
  }

  &.Novice {
    border: 3px solid var(--color-novice);
  }
}

.leaderboard-table .team-badge {
  display: flex;
  margin-left: 2px;
  margin-bottom: 0;
  width: 15px;
}

.leaderboard-table .inverted {
  filter: invert(1);
}

.leaderboard-table .data.stake {
  color: var(--color-subtle);
}

.leaderboard-table .no-returns {
  color: var(--color-tertiary);
}

.leaderboard-table .data {
  color: var(--color-secondary);

  overflow: hidden;
  text-align: right;
}

.leaderboard-table .true-contribution {
  color: var(--color-true-contribution);
  font-weight: 500;
}

.leaderboard-table .mmc {
  color: var(--color-mmc);
  font-weight: 500;
}

.leaderboard-title {
  font-weight: 500;
  font-family: var(--brand-font);
  font-size: 16px;
  text-transform: uppercase;
}

.search {
  display: flex;
  align-items: center;
  border: 1px solid var(--color-button-control-background-border);
  border-radius: 4px;
  padding: 10px 5px;
  max-height: 40px;
}

.search-icon {
  color: var(--color-secondary);
}

.search-icon.theme--light.v-icon {
  color: var(--color-primary) !important;
}

.search-input {
  color: var(--color-secondary);
  margin-left: 10px;
  font-size: 16px;
  width: 100%;
}

.search-input:focus {
  outline: none;
}

@media screen and (max-width: 700px) {
  .search {
    margin-top: 20px;
  }
}

.leaderboard-table .placeholder {
  color: var(--color-secondary);
  width: 100%;
  min-height: 200px;
  background-color: var(--paper-background-color);
  opacity: 0.6;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.no-leaderboard-search-results {
  display: flex;
  justify-content: center;
  background-color: var(--paper-background-color);
  border: 1px solid var(--paper-background-color);
  padding: 2em;
  margin: 0;
  margin-top: -4px;
  color: var(--color-tertiary);
  font-weight: 500;
  font-family: var(--ui-font);
}

.leaderboard-table .clickable:hover {
  cursor: pointer;
  color: var(--color-primary);
}

.leaderboard-table .table-cell.neutral {
  color: var(--color-secondary);
}

.leaderboard-table .table-cell.zero {
  color: var(--color-subtle);
}
.leaderboard-table .table-header-cell.colSelected {
  color: var(--color-primary);
  white-space: nowrap;
  text-shadow: 0 0 5px #fff5;
}
.leaderboard-table .table-header-cell.colSelected.true-contribution {
  color: var(--color-true-contribution);
  font-weight: 700;
}

.leaderboard-table .table-header-cell.colSelected.mmc {
  color: var(--color-mmc);
  font-weight: 700;
}

.leaderboard-table .table-header-cell.colSelected:before {
  font-size: 0.8em;
  content: '▼ ';
  margin-right: 0.4em;
  margin-bottom: 10px;
  padding-bottom: 2px;
  white-space: nowrap;
}

.leaderboard-table .table-header-cell.colSelected.sortReversed:before {
  content: '▲ ';
}

.leaderboard-table .table-cell-link {
  height: 100%;
  width: 100%;
  display: block;
  text-decoration: none;
  outline: none;
}

@media screen and (max-width: 599px) {
  .tabs {
    margin-top: 20px;
  }

  .leaderboard-search-box {
    display: none;
  }
}
</style>
